import type { MultiValue, Option } from '@ksyos/design-system';
import type { JSX } from 'react';
import { Card, IconButton } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ServiceOrganizationRole as Role } from '@common/enums';
import type { ServiceOrganizationSettingRole } from '@common/interfaces/api/serviceOrganizationSettings';
import { ManagerAuthorization } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { joinArrayWithConjunction } from '@common/utils/textUtils';
import { ServiceOrganizationSettingButtons } from '@frontend/components/pages/organizations/serviceSettings/serviceOrganizationSetting/ServiceOrganizationSettingButtons';
import { ServiceOrganizationSettingFormMode } from '@frontend/components/pages/organizations/serviceSettings/types';
import { getDistributions } from '@frontend/services/api/serviceOrganizationRolesApiService';
import { getRoles, updateRoles } from '@frontend/services/api/serviceOrganizationSettingApiService';
import { getLanguageCode, getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useServiceOrganizationSettingContext } from '@frontend/utils/hooks/useServiceOrganizationsSettingsContext';
import { ServiceOrganizationRolesEdit } from './ServiceOrganizationRolesEdit';
import { ServiceOrganizationRolesRead } from './ServiceOrganizationRolesRead';

type ServiceOrganizationRolesProps = {
    onChange: (roles: Role[]) => void;
    roles: Role[];
};

export function ServiceOrganizationRoles(props: ServiceOrganizationRolesProps): JSX.Element | null {
    const { onChange, roles } = props;
    const abortSignal = useAbortSignal();
    const [errors, setErrors] = useState<Map<Role, string>>(new Map());
    const { serviceOrganizationSetting } = useServiceOrganizationSettingContext();
    const [currentServiceOrganizationSettingRoles, setCurrentServiceOrganizationSettingRoles] = useState<
        ServiceOrganizationSettingRole[] | undefined
    >(undefined);
    const [isSaving, setIsSaving] = useState(false);
    const [mode, setMode] = useState(ServiceOrganizationSettingFormMode.read);
    const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);
    const canEdit = hasOneAuthorization(ManagerAuthorization.organizationAddEdit);
    const languageFile = getSelectedLanguage();

    useEffect(() => {
        loadServiceOrganizationRoles();
    }, []);

    if (!currentServiceOrganizationSettingRoles) {
        return null;
    }

    return (
        <Card>
            <Card.Transition type={mode} />
            <Card.Header
                action={
                    canEdit && (
                        <IconButton
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => setMode(ServiceOrganizationSettingFormMode.edit)}
                        />
                    )
                }
                title={languageFile.serviceOrganizationSetting['role(s)OfOrganization']}
            />
            <Card.Body>
                {mode === 'read' && (
                    <ServiceOrganizationRolesRead
                        currentServiceOrganizationSettingRoles={currentServiceOrganizationSettingRoles}
                    />
                )}
                {mode === 'edit' && (
                    <>
                        <ServiceOrganizationRolesEdit
                            currentServiceOrganizationSettingRoles={currentServiceOrganizationSettingRoles}
                            errors={errors}
                            onChange={handleChange}
                            roles={roles}
                        />
                        <ServiceOrganizationSettingButtons
                            isSaving={isSaving}
                            mode={mode}
                            onClose={handleClose}
                            onSave={handleSubmit}
                        />
                    </>
                )}
            </Card.Body>
        </Card>
    );

    function mapServiceOrganizationRoleToRole(serviceOrganizationSettingRole: ServiceOrganizationSettingRole): Role {
        return serviceOrganizationSettingRole.role;
    }

    async function loadServiceOrganizationRoles(): Promise<void> {
        const { items } = await getRoles(serviceOrganizationSetting.id, abortSignal);
        setCurrentServiceOrganizationSettingRoles(items);
        const nextSelectedRoles = items.map(mapServiceOrganizationRoleToRole);
        setSelectedRoles(nextSelectedRoles);
        onChange(nextSelectedRoles);
    }

    async function handleChange(options: MultiValue<Option>): Promise<void> {
        const nextSelectedRoles = options.map((option) => option.value as Role);
        setSelectedRoles(nextSelectedRoles);
        await validateOnChangeOrClose(nextSelectedRoles);
        onChange(nextSelectedRoles);
    }

    async function handleClose(): Promise<void> {
        // Reset selected roles
        const nextSelectedRoles = currentServiceOrganizationSettingRoles?.map(mapServiceOrganizationRoleToRole) ?? [];
        setSelectedRoles(nextSelectedRoles);
        onChange(nextSelectedRoles);
        await validateOnChangeOrClose(nextSelectedRoles);
        setMode(ServiceOrganizationSettingFormMode.read);
    }

    async function validateOnChangeOrClose(newSelectedRoles: Role[]): Promise<void> {
        const validationErrors = errors;
        for (const role of newSelectedRoles) {
            if (errors.has(role)) {
                validationErrors.delete(role);
            }
        }
        setErrors(validationErrors);
    }

    async function handleSubmit(): Promise<void> {
        setIsSaving(true);
        const validationErrors = await validateSelectedRoles();
        if (validationErrors && validationErrors.size > 0) {
            setErrors(validationErrors);

            return setIsSaving(false);
        }
        await updateRoles(serviceOrganizationSetting.id, { roles: selectedRoles }, abortSignal);
        await loadServiceOrganizationRoles();
        setIsSaving(false);
        setMode(ServiceOrganizationSettingFormMode.read);
    }

    async function validateSelectedRoles(): Promise<Map<Role, string> | undefined> {
        if (!currentServiceOrganizationSettingRoles) {
            return;
        }

        const validationErrors = new Map();
        for (const currentServiceOrganizationSettingRole of currentServiceOrganizationSettingRoles) {
            // Role has not been deleted
            if (selectedRoles.includes(currentServiceOrganizationSettingRole.role)) {
                continue;
            }

            const organizations = await getOrganizationsWhereRoleIsDistributed(
                currentServiceOrganizationSettingRole.id
            );

            if (organizations.length === 0) {
                continue;
            }

            validationErrors.set(
                currentServiceOrganizationSettingRole.role,
                formatString(languageFile.serviceOrganizationSettingRoleDistributions.cannotDeleteRole, [
                    languageFile.serviceOrganizationRoleEnum[currentServiceOrganizationSettingRole.role],
                    joinArrayWithConjunction(organizations, getLanguageCode()),
                ])
            );
        }

        return validationErrors;
    }

    async function getOrganizationsWhereRoleIsDistributed(serviceOrganizationRoleId: string): Promise<string[]> {
        const { items } = await getDistributions(serviceOrganizationRoleId, abortSignal);

        return items.map((item) => item.serviceOrganizationSetting.organization.name);
    }
}
